import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Plus, Copy, Sparkles, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, formatVND, formatCompact } from '@/lib/utils';
import { suggestBudget, legacyBudgetLimits, type AnnualItem } from '../../../finance';
import { saveEnvelope } from '../../../ledger/plans';
import { Link } from 'react-router-dom';
import { useData } from '../../store/data';
import Pill from '../../components/kit/Pill';
import { useUnclassified } from '../settings/classify';
import { parseMoneyOrNull } from '../entry/formSchema';
import { useBudgetMonth, useRule503020, monthKey } from './useBudget';

const TONE = { ok: 'bg-good', warn: 'bg-warn', bad: 'bg-bad', none: 'bg-muted' } as const;

export default function EnvelopeTab({ year, month, header }: { year: number; month: number; header?: ReactNode }) {
  const b = useBudgetMonth(year, month);
  const { expenses, expenseCategories, budgetEnvelopes, budgets } = useData(s => s.data);
  const r = useRule503020(year, month);
  const unclassified = useUnclassified();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [rollover, setRollover] = useState(false);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);
  const [annual, setAnnual] = useState<AnnualItem[]>([]);

  // Nạp lại bản nháp mỗi khi đổi tháng hoặc dữ liệu lưu thay đổi
  useEffect(() => {
    setDraft(Object.fromEntries(Object.entries(b.limits).map(([k, v]) => [k, String(v)])));
    setRollover(b.rollover);
  }, [b.key, b.source, JSON.stringify(b.limits), b.rollover]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsed = useMemo(() => Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, parseMoneyOrNull(v)])), [draft]);
  const invalid = Object.keys(draft).some(k => draft[k].trim() !== '' && parsed[k] === null);
  const dirty = JSON.stringify(Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, v ?? 0]).filter(([, v]) => (v as number) > 0))) !== JSON.stringify(b.limits) || rollover !== b.rollover || b.source === 'legacy';

  const catNames: string[] = useMemo(() => [...new Set<string>(expenseCategories.map((c: any) => c.name))], [expenseCategories]);
  const addable = catNames.filter(n => !(n in draft));
  const lineBy = new Map(b.status.lines.map(l => [l.category, l]));

  const save = async () => {
    setBusy(true);
    const limits = Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, v ?? 0]));
    const res = await saveEnvelope(getAuth().currentUser!.uid, b.key, { limits, rollover });
    setBusy(false);
    res.success ? toast.success(`Đã lưu ngân sách tháng ${month}/${year}`) : toast.error(res.error ?? 'Không lưu được');
  };

  const fill = (limits: Record<string, number>) => setDraft(Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, String(v)])));
  const copyPrev = () => {
    const d = new Date(year, month - 2, 1);
    const k = monthKey(d.getFullYear(), d.getMonth() + 1);
    const env = budgetEnvelopes.find((e: any) => e.id === k);
    const legacy = budgets.find((x: any) => x.year === d.getFullYear() && x.month === d.getMonth() + 1);
    const lim = env?.limits ?? (legacy ? legacyBudgetLimits(legacy) : null);
    lim && Object.keys(lim).length ? fill(lim) : toast.info('Tháng trước chưa có ngân sách để sao chép.');
  };
  const suggest = () => {
    const { limits: lim, annual: yearly } = suggestBudget(expenses, new Date(year, month - 1, 15));
    setAnnual(yearly);
    Object.keys(lim).length ? fill(lim) : toast.info('Chưa đủ chi tiêu các tháng trước để gợi ý.');
  };

  const t = b.status.totals;
  const classPct = (v: number | null) => (v === null ? '—' : `${Math.round(v)}%`);

  return (
    <div className="space-y-5">
      {b.source === 'legacy' && (
        <p className="rounded-md bg-warn-soft px-4 py-2.5 text-sm text-warn">Đang hiển thị ngân sách <b>kiểu cũ</b> của tháng này (chỉ đọc). Chỉnh nếu cần rồi bấm <b>Lưu</b> để chuyển thành ngân sách phong bì — dữ liệu cũ vẫn được giữ nguyên.</p>
      )}

      <Card className="overflow-hidden">
        {header}
        <CardContent className="grid gap-4 p-4 sm:grid-cols-[1fr_auto] sm:items-center sm:p-5">
          <div>
            <p className="text-muted-foreground">Đã dùng so với tổng hạn mức đã đặt</p>
            <p className="font-slab num text-4xl font-bold">{t.pct === null ? '—' : `${Math.round(t.pct)}%`}</p>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={`Đã dùng ${Math.round(t.pct ?? 0)}% ngân sách`}>
              <div className={cn('h-full rounded-full transition-[width] duration-700', TONE[t.level])} style={{ width: `${Math.min(100, t.pct ?? 0)}%` }} />
            </div>
          </div>
          <dl className="num grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-1 sm:text-right">
            <div><dt className="inline text-muted-foreground">Hạn mức </dt><dd className="inline font-medium">{formatCompact(t.available)}</dd></div>
            <div><dt className="inline text-muted-foreground">Đã chi </dt><dd className="inline font-medium">{formatCompact(t.spent)}</dd></div>
            <div><dt className="inline text-muted-foreground">Đã cam kết </dt><dd className="inline font-medium">{formatCompact(t.committed)}</dd></div>
            <div><dt className="inline text-muted-foreground">Còn lại </dt><dd className={cn('inline font-semibold', t.remaining < 0 && 'text-bad')}>{formatCompact(t.remaining)}</dd></div>
          </dl>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={copyPrev}><Copy size={14} aria-hidden /> Sao chép tháng trước</Button>
        <Button variant="outline" size="sm" onClick={suggest}><Sparkles size={14} aria-hidden /> Gợi ý từ 3 tháng gần nhất</Button>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={rollover} onChange={e => setRollover(e.target.checked)} />
          Chuyển phần dư sang tháng sau
        </label>
      </div>

      {annual.length > 0 && (
        <div className="rounded-md bg-muted px-4 py-3 text-sm">
          <p className="font-medium">Khoản đóng theo năm — không đặt hạn mức tháng để khỏi báo vượt giả</p>
          <ul className="num mt-1 space-y-0.5 text-muted-foreground">
            {annual.map(a => <li key={a.category}>{a.category}: {formatCompact(a.yearTotal)}/năm — nên để dành khoảng {formatCompact(a.perMonth)}/tháng</li>)}
          </ul>
        </div>
      )}
      {Object.keys(draft).length > 0 && <p className="text-sm text-muted-foreground">Gợi ý chỉ phản ánh mức chi thực tế gần đây, chưa phải mức nên đạt. Hãy hạ từng dòng cho vừa thu nhập sau khi trả nợ.</p>}

      <Card className="divide-y divide-border overflow-hidden p-0">
        {Object.keys(draft).length === 0 && <p className="p-6 text-center text-muted-foreground">Chưa đặt hạn mức nào. Thêm danh mục bên dưới, hoặc dùng "Gợi ý từ 3 tháng gần nhất".</p>}
        {Object.keys(draft).map(cat => {
          const l = lineBy.get(cat);
          const shownLevel = l?.level ?? 'none';
          return (
            <div key={cat} className="grid items-center gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[1fr_9rem]">
              <div className="min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="truncate text-base font-medium">{cat}
                    {l && l.carry > 0 && <span className="ml-2 text-xs font-normal text-good">+{formatCompact(l.carry)} chuyển sang</span>}
                    {l?.level === 'warn' && <Pill tone="warn" className="ml-2">Sắp hết</Pill>}
                    {l?.level === 'bad' && <Pill tone="bad" className="ml-2">Vượt</Pill>}
                  </p>
                  {l && <p className={cn('num shrink-0 text-sm', l.remaining < 0 ? 'text-bad' : 'text-muted-foreground')}>{l.remaining < 0 ? `vượt ${formatCompact(-l.remaining)}` : `còn ${formatCompact(l.remaining)}`}</p>}
                </div>
                <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-muted">
                  <div className={cn('h-full', TONE[shownLevel])} style={{ width: `${Math.min(100, l && l.available > 0 ? (Math.max(0, l.spent) / l.available) * 100 : 0)}%` }} />
                  <div className="h-full bg-primary/30" style={{ width: `${Math.min(100, l && l.available > 0 ? (l.committed / l.available) * 100 : 0)}%` }} />
                </div>
                {l && <p className="num mt-1 text-sm text-muted-foreground">Đã chi {formatCompact(l.spent)}{l.committed > 0 && ` · cam kết ${formatCompact(l.committed)}`}</p>}
              </div>
              <div>
                <Input value={draft[cat]} onChange={e => setDraft(d => ({ ...d, [cat]: e.target.value }))} inputMode="text" className="num text-right"
                  aria-label={`Hạn mức ${cat}`} aria-invalid={parsed[cat] === null} placeholder="0" />
                {parsed[cat] === null && <p className="mt-1 text-sm text-bad">Số không hợp lệ</p>}
              </div>
            </div>
          );
        })}
        {addable.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-3">
            <Plus size={14} className="text-muted-foreground" aria-hidden />
            <Select value={adding} onValueChange={(v) => { setDraft(d => ({ ...d, [v]: '' })); setAdding(''); }}>
              <SelectTrigger className="max-w-64"><SelectValue placeholder="Thêm danh mục vào ngân sách" /></SelectTrigger>
              <SelectContent>{addable.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Hạn mức để trống hoặc 0 = bỏ khỏi ngân sách. Cam kết = dự chi đang chờ xác nhận của tháng.</p>
        <Button onClick={save} disabled={busy || !dirty || invalid}><Save size={14} aria-hidden /> {busy ? 'Đang lưu…' : 'Lưu ngân sách'}</Button>
      </div>

      {b.status.unbudgeted.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Chi nhưng chưa đặt hạn mức</CardTitle></CardHeader>
          <CardContent><ul className="num space-y-1 text-sm">{b.status.unbudgeted.map(u => <li key={u.category} className="flex justify-between"><span>{u.category}</span><span>{formatVND(u.spent)}</span></li>)}</ul></CardContent></Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">Quy tắc 50/30/20 — tháng {month}/{year}</CardTitle></CardHeader>
        <CardContent>
          {unclassified.length > 0 && (
            <p className="mb-3 rounded-md bg-warn-soft px-3 py-2 text-sm text-warn">
              {unclassified.length} danh mục chi chưa phân loại nên đang tính là "Mong muốn". <Link to="/v2/cai-dat" className="font-medium underline">Phân loại trong Cài đặt</Link>
            </p>
          )}
          {r.income <= 0 ? <p className="text-sm text-muted-foreground">Chưa có thu nhập tháng này — quy tắc cần thu nhập làm mẫu số.</p> : (
            <div className="space-y-3 text-sm">
              {[
                { l: 'Thiết yếu', v: r.needs, p: r.needsPct, target: 50, hint: 'nên ≤ 50%', bad: (r.needsPct ?? 0) > 50 },
                { l: 'Mong muốn', v: r.wants, p: r.wantsPct, target: 30, hint: 'nên ≤ 30%', bad: (r.wantsPct ?? 0) > 30 },
                { l: 'Tiết kiệm', v: r.savings, p: r.savingsPct, target: 20, hint: 'nên ≥ 20%', bad: (r.savingsPct ?? 0) < 20 }
              ].map(x => (
                <div key={x.l}>
                  <div className="flex items-baseline justify-between"><span>{x.l} <span className="text-sm text-muted-foreground">({x.hint})</span></span><span className={cn('num font-medium', x.bad ? 'text-warn' : 'text-good')}>{classPct(x.p)} · {formatCompact(x.v)}</span></div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className={cn('h-full rounded-full', x.bad ? 'bg-warn' : 'bg-good')} style={{ width: `${Math.max(0, Math.min(100, x.p ?? 0))}%` }} /></div>
                </div>
              ))}
              <p className="text-sm text-muted-foreground">Phân loại theo nhãn Thiết yếu/Mong muốn của từng danh mục (Cài đặt); danh mục chưa gán tính là Mong muốn. Tiền vay không tính là thu nhập.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
